import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { UserIcon, EnvelopeIcon, LockClosedIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { API_URLS } from '../config/api';
import ChurchSelect from '../components/ChurchSelect';

function Register() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    userType: 'rider',
    churchId: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [churchLoading, setChurchLoading] = useState(true);
  const [defaultChurchName, setDefaultChurchName] = useState('');
  const [showChurchPicker, setShowChurchPicker] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function resolveHomeChurch() {
      try {
        const single = await fetch(API_URLS.church);
        if (single.ok) {
          const church = await single.json();
          if (church?.id && !cancelled) {
            setFormData((prev) => ({ ...prev, churchId: church.id }));
            setDefaultChurchName(church.name || '');
            setShowChurchPicker(false);
            return;
          }
        }
      } catch {
        /* fall through to multi-church picker */
      }

      try {
        const list = await fetch(API_URLS.churches);
        if (list.ok) {
          const churches = await list.json();
          if (!cancelled && Array.isArray(churches) && churches.length === 1) {
            setFormData((prev) => ({ ...prev, churchId: churches[0].id }));
            setDefaultChurchName(churches[0].name || '');
            setShowChurchPicker(false);
            return;
          }
        }
      } catch {
        /* show picker */
      }

      if (!cancelled) {
        setShowChurchPicker(true);
      }
    }

    resolveHomeChurch().finally(() => {
      if (!cancelled) setChurchLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Basic validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (churchLoading) {
      setError('Loading church information — please wait a moment and try again.');
      setLoading(false);
      return;
    }

    if (!formData.churchId) {
      setError('Please select your home church');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(API_URLS.register, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Church-ID': formData.churchId,
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          phone: formData.phone,
          userType: formData.userType,
          churchId: formData.churchId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      localStorage.setItem('churchId', formData.churchId);

      // Navigate to phone verification if registration requires phone verification
      if (data.requiresPhoneVerification) {
        navigate('/verify-phone', {
          state: {
            userId: data.userId,
            email: data.email,
            phone: data.phone,
            message: data.message
          }
        });
      } else {
        // Fallback to login if no phone verification required
        navigate('/login');
      }
    } catch (err) {
      setError(err.message || 'Failed to create an account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500">
      <div className="container mx-auto px-4 h-full">
        <div className="flex content-center items-center justify-center h-screen">
          <div className="w-full lg:w-5/12 px-4">
            <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-2xl bg-white border-0">
              <div className="rounded-t-2xl mb-0 px-8 pt-8">
                <div className="text-center mb-3">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Join Chariots of Fire
                  </h1>
                  <p className="text-gray-600 text-sm mb-4">
                    Create an account to start your journey
                  </p>
                </div>
              </div>

              <div className="flex-auto px-8 pb-8 pt-0">
                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-center">
                    {error}
                  </div>
                )}

                <form className="space-y-5" onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="relative">
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                        First Name
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <UserIcon className="h-5 w-5 text-indigo-500" />
                        </div>
                        <input
                          id="firstName"
                          name="firstName"
                          type="text"
                          required
                          className="appearance-none block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder="John"
                        />
                      </div>
                    </div>

                    <div className="relative">
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <UserIcon className="h-5 w-5 text-indigo-500" />
                        </div>
                        <input
                          id="lastName"
                          name="lastName"
                          type="text"
                          required
                          className="appearance-none block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder="Doe"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="relative">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <EnvelopeIcon className="h-5 w-5 text-indigo-500" />
                      </div>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        className="appearance-none block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john.doe@example.com"
                      />
                    </div>
                  </div>

                  <div className="relative">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <PhoneIcon className="h-5 w-5 text-indigo-500" />
                      </div>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        className="appearance-none block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  <div className="relative">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <LockClosedIcon className="h-5 w-5 text-indigo-500" />
                      </div>
                      <input
                        id="password"
                        name="password"
                        type="password"
                        required
                        className="appearance-none block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="••••••••"
                      />
                    </div>
                  </div>

                  <div className="relative">
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <LockClosedIcon className="h-5 w-5 text-indigo-500" />
                      </div>
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        required
                        className="appearance-none block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="••••••••"
                      />
                    </div>
                  </div>

                  {showChurchPicker ? (
                    <div className="relative">
                      <label htmlFor="churchId" className="block text-sm font-medium text-gray-700 mb-1">
                        Home Church
                      </label>
                      <ChurchSelect
                        id="churchId"
                        name="churchId"
                        value={formData.churchId}
                        onChange={handleChange}
                        required
                        className="block w-full pl-3 pr-10 py-2.5 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-xl"
                      />
                    </div>
                  ) : (
                    defaultChurchName && (
                      <p className="text-sm text-gray-600 bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3">
                        Home church: <span className="font-medium text-gray-900">{defaultChurchName}</span>
                      </p>
                    )
                  )}

                  <div className="relative">
                    <label htmlFor="userType" className="block text-sm font-medium text-gray-700 mb-1">
                      I want to
                    </label>
                    <select
                      id="userType"
                      name="userType"
                      className="block w-full pl-3 pr-10 py-2.5 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-xl"
                      value={formData.userType}
                      onChange={handleChange}
                    >
                      <option value="rider">Book Rides (Rider)</option>
                      <option value="driver">Offer Rides (Driver)</option>
                    </select>
                  </div>

                  <div className="text-center mt-6">
                    <button
                      type="submit"
                      disabled={loading || churchLoading || !formData.churchId}
                      className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-all duration-200"
                    >
                      {loading ? (
                        <div className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Creating Account...
                        </div>
                      ) : (
                        'Create Account'
                      )}
                    </button>
                  </div>
                </form>

                <div className="text-center mt-6">
                  <p className="text-sm text-gray-600">
                    Already have an account?{' '}
                    <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-500">
                      Sign in
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;
